/**
 * Product Library Service
 * Handles all API interactions with the product_library table
 */

import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { getOrganizationContext, requireAuthenticatedContext } from '@/lib/auth';
import { logger } from '@/lib/logger';
import type {
  ProductLibraryItem,
  CreateProductLibraryItemDto,
  UpdateProductLibraryItemDto,
  ProductSearchParams,
  ProductSearchResult,
  ProductCategory,
} from '../types';
import {
  CreateProductLibraryItemSchema,
  UpdateProductLibraryItemSchema,
  ProductSearchParamsSchema,
} from '../schemas/validation';

class ProductLibraryService {
  private async getClient(): Promise<any> {
    return getAuthenticatedSupabaseClient();
  }

  /**
   * Get user's organizations from Clerk
   */
  private async getUserOrganizations(): Promise<string[]> {
    try {
      const { organizationId } = await getOrganizationContext();
      return organizationId ? [organizationId] : [];
    } catch (error) {
      logger.error('Error getting user organizations:', error);
      return [];
    }
  }

  /**
   * Search products with filters - now includes cross-org products
   */
  async search(params: ProductSearchParams): Promise<ProductSearchResult> {
    const validatedParams = ProductSearchParamsSchema.parse(params);
    const {
      query,
      category,
      subcategory,
      brand,
      minPrice,
      maxPrice,
      is_public,
      is_verified,
      limit = 20,
      offset = 0,
    } = validatedParams;

    const { userId, organizationId } = await getOrganizationContext();
    const userOrgs = organizationId ? [organizationId] : await this.getUserOrganizations();

    logger.log('[ProductLibrary] Search params:', { query, userId, organizationId, userOrgs });
    const supabase = await this.getClient();

    // RLS policies on product_library handle visibility filtering automatically
    // The authenticated Supabase client passes the Clerk JWT which RLS uses
    // We just need to apply the search filters here
    let queryBuilder = supabase.from('product_library').select('*', { count: 'exact' });

    // Apply search filter - search across brand, model, description, category, and subcategory
    // This enables searching for generic terms like "dome camera" or "invisible speaker"
    if (query) {
      queryBuilder = queryBuilder.or(
        `brand.ilike.%${query}%,model.ilike.%${query}%,description.ilike.%${query}%,category.ilike.%${query}%,subcategory.ilike.%${query}%`,
      );
    }
    if (category) {
      queryBuilder = queryBuilder.eq('category', category);
    }
    if (subcategory) {
      queryBuilder = queryBuilder.eq('subcategory', subcategory);
    }
    if (brand) {
      queryBuilder = queryBuilder.eq('brand', brand);
    }
    if (minPrice !== undefined) {
      queryBuilder = queryBuilder.gte('price', minPrice);
    }
    if (maxPrice !== undefined) {
      queryBuilder = queryBuilder.lte('price', maxPrice);
    }
    if (is_verified !== undefined) {
      queryBuilder = queryBuilder.eq('is_verified', is_verified);
    }

    // Apply pagination
    queryBuilder = queryBuilder
      .range(offset, offset + limit - 1)
      .order('brand', { ascending: true })
      .order('model', { ascending: true });

    const { data, error, count } = await queryBuilder;

    logger.log('[ProductLibrary] Search results:', {
      count,
      itemCount: data?.length || 0,
      error: error?.message,
      query,
    });

    if (error) {
      logger.error('Error searching products:', error);
      throw new Error(`Failed to search products: ${error.message}`);
    }

    return {
      items: data || [],
      total: count || 0,
      limit,
      offset,
    };
  }

  /**
   * Get a single product by ID
   */
  async getById(id: string): Promise<ProductLibraryItem | null> {
    const supabase = await this.getClient();
    const { data, error } = await supabase
      .from('product_library')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      logger.error('Error fetching product:', error);
      throw new Error(`Failed to fetch product: ${error.message}`);
    }

    return data;
  }

  /**
   * Get products by category
   */
  async getByCategory(category: ProductCategory, limit = 50): Promise<ProductLibraryItem[]> {
    // Get the current organization ID for scoped search
    const { userId, organizationId } = await getOrganizationContext();
    const supabase = await this.getClient();

    let queryBuilder = supabase.from('product_library').select('*').eq('category', category);

    // Build visibility filter - user sees:
    // 1. Public products
    // 2. Products from their organization
    // 3. Products owned by their user ID or organization (for user_global products)
    const visibilityConditions = ['is_public.eq.true'];

    if (organizationId) {
      visibilityConditions.push(`organization_id.eq.${organizationId}`);
      visibilityConditions.push(`owner_id.eq.${organizationId}`);
    }

    if (userId) {
      // owner_id is TEXT type, so we can compare with Clerk user ID
      visibilityConditions.push(`owner_id.eq.${userId}`);
    }

    queryBuilder = queryBuilder.or(visibilityConditions.join(','));

    const { data, error } = await queryBuilder
      .order('brand', { ascending: true })
      .order('model', { ascending: true })
      .limit(limit);

    if (error) {
      logger.error('Error fetching products by category:', error);
      throw new Error(`Failed to fetch products: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Create a new product - automatically sets cross-org visibility
   */
  async create(dto: CreateProductLibraryItemDto): Promise<ProductLibraryItem> {
    logger.log('[productLibraryService.create] Creating product with:', dto);

    let validated;
    try {
      validated = CreateProductLibraryItemSchema.parse(dto);
    } catch (validationError: any) {
      logger.error(
        '[productLibraryService.create] Validation failed:',
        validationError.errors || validationError,
      );
      throw new Error(
        `Validation failed: ${validationError.message || JSON.stringify(validationError.errors)}`,
      );
    }

    const context = await requireAuthenticatedContext();
    const userId = context?.userId;
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const organizationId = context?.organizationId ?? null;
    const userOrgs = organizationId ? [organizationId] : await this.getUserOrganizations();
    const supabase = await this.getClient();

    // Require organization context for product creation
    if (!organizationId) {
      throw new Error('Organization context required to create products');
    }

    const { data, error } = await supabase
      .from('product_library')
      .insert({
        ...validated,
        // Organization ownership (not user)
        ownership_type: 'organization',
        owner_id: organizationId,
        organization_id: organizationId,
        visibility_scope: 'org_only',
        // Make accessible to all user's orgs for convenience
        portal_mapping_org_ids: userOrgs,
        // Source tracking
        source: validated.source || 'manual',
        creation_method: validated.creation_method || 'manual',
        // Creator tracking - use portal_mapping_user_id for Clerk user ID (text column)
        // Note: created_by is UUID type for Supabase Auth, not Clerk
        portal_mapping_user_id: userId,
      })
      .select()
      .single();

    if (error) {
      logger.error('Error creating product:', error);
      throw new Error(`Failed to create product: ${error.message}`);
    }

    return data;
  }

  /**
   * Update an existing product
   */
  async update(id: string, dto: UpdateProductLibraryItemDto): Promise<ProductLibraryItem> {
    const validated = UpdateProductLibraryItemSchema.parse(dto);
    const supabase = await this.getClient();

    const { data, error } = await supabase
      .from('product_library')
      .update(validated)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      logger.error('Error updating product:', error);
      throw new Error(`Failed to update product: ${error.message}`);
    }

    return data;
  }

  /**
   * Delete a product
   */
  async delete(id: string): Promise<void> {
    const supabase = await this.getClient();
    const { error } = await supabase.from('product_library').delete().eq('id', id);

    if (error) {
      logger.error('Error deleting product:', error);
      throw new Error(`Failed to delete product: ${error.message}`);
    }
  }

  /**
   * Get all unique brands
   */
  async getBrands(): Promise<string[]> {
    const supabase = await this.getClient();
    const { data, error } = await supabase
      .from('product_library')
      .select('brand')
      .eq('is_public', true)
      .order('brand', { ascending: true });

    if (error) {
      logger.error('Error fetching brands:', error);
      throw new Error(`Failed to fetch brands: ${error.message}`);
    }

    // Extract unique brands
    const brands = [...new Set(data?.map(item => item.brand) || [])];
    return brands;
  }

  /**
   * Get popular/featured products
   */
  async getFeatured(limit = 10): Promise<ProductLibraryItem[]> {
    const supabase = await this.getClient();
    const { data, error } = await supabase
      .from('product_library')
      .select('*')
      .eq('is_public', true)
      .eq('is_verified', true)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      logger.error('Error fetching featured products:', error);
      throw new Error(`Failed to fetch featured products: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Check if a product exists by brand and model
   */
  async exists(brand: string, model: string): Promise<boolean> {
    const supabase = await this.getClient();
    const { count, error } = await supabase
      .from('product_library')
      .select('*', { count: 'exact', head: true })
      .eq('brand', brand)
      .eq('model', model);

    if (error) {
      logger.error('Error checking product existence:', error);
      throw new Error(`Failed to check product existence: ${error.message}`);
    }

    return (count || 0) > 0;
  }

  /**
   * Get a single product by ID (alias for getById)
   */
  async getProduct(id: string): Promise<ProductLibraryItem | null> {
    return this.getById(id);
  }

  /**
   * Update product dimensions (for elevation view integration)
   * Used when user adds a product to elevation and sets dimensions
   */
  async updateDimensions(
    id: string,
    dimensions: {
      height_mm?: number;
      width_mm?: number;
      depth_mm?: number;
    },
  ): Promise<ProductLibraryItem> {
    const supabase = await this.getClient();

    // Only include dimensions that are provided
    const updateData: Record<string, number> = {};
    if (dimensions.height_mm !== undefined) updateData.height_mm = dimensions.height_mm;
    if (dimensions.width_mm !== undefined) updateData.width_mm = dimensions.width_mm;
    if (dimensions.depth_mm !== undefined) updateData.depth_mm = dimensions.depth_mm;

    if (Object.keys(updateData).length === 0) {
      throw new Error('At least one dimension must be provided');
    }

    const { data, error } = await supabase
      .from('product_library')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      logger.error('Error updating product dimensions:', error);
      throw new Error(`Failed to update product dimensions: ${error.message}`);
    }

    return data;
  }

  /**
   * Update product custom icon
   * Used when user customizes icon from element drawer
   */
  async updateCustomIcon(
    id: string,
    iconData: {
      custom_icon_svg?: string | null;
      custom_icon_abbreviation?: string | null;
      custom_icon_fill_color?: string | null;
      custom_icon_border_color?: string | null;
      custom_icon_text_color?: string | null;
      custom_icon_shape?: string | null;
    },
  ): Promise<ProductLibraryItem> {
    const supabase = await this.getClient();

    const { data, error } = await supabase
      .from('product_library')
      .update({
        custom_icon_svg: iconData.custom_icon_svg,
        custom_icon_abbreviation: iconData.custom_icon_abbreviation,
        custom_icon_fill_color: iconData.custom_icon_fill_color,
        custom_icon_border_color: iconData.custom_icon_border_color,
        custom_icon_text_color: iconData.custom_icon_text_color,
        custom_icon_shape: iconData.custom_icon_shape,
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      logger.error('Error updating product custom icon:', error);
      throw new Error(`Failed to update product icon: ${error.message}`);
    }

    return data;
  }

  /**
   * Check if a product has dimensions set
   */
  hasValidDimensions(product: ProductLibraryItem): boolean {
    return !!(
      product.width_mm &&
      product.width_mm > 0 &&
      product.height_mm &&
      product.height_mm > 0
    );
  }
}

export const productLibraryService = new ProductLibraryService();
